// Server-Sent Events bridge to the Go /stream endpoint.
//
// ONE multiplexed EventSource for the whole app. Components subscribe to
// the channels they need via useEventStream(channels, cb); the hub tracks
// the union (ref-counted), (re)opens a single connection to
// /stream?channels=<union>, and fans each incoming event out to the
// subscribers whose channel set includes it. useSseStatus() / the "live"
// dot read the shared connection state without opening anything of their
// own.
//
// Why one connection: HTTP/1.1 caps ~6 connections per origin (the Vite
// dev server), and a per-hook EventSource each would burn that budget;
// over HTTP/2 (prod Apache, `Protocols h2`) it's multiplexed anyway, but
// one is still tidier. Reconnecting when the channel set changes is
// unavoidable — you can't add channels to a live EventSource — but it's
// debounced one tick, so a page mounting several subscribers reconnects
// only once.
//
// The Go side names each event after its channel ("routers", "olts",
// "router:Bashail", …) with a JSON `data` payload — same stream the
// legacy htmx pages used. React pattern: subscribe → on each event run a
// callback, typically `queryClient.invalidateQueries(...)` so the
// relevant TanStack Query data re-fetches and the UI re-renders.
//
// Auth: EventSource can't send the bearer header, but /v1/api/auth/login
// (and /refresh, /me) also sets the `nocstream` HMAC cookie, and
// withCredentials:true sends it — so /stream authenticates the same way
// the browser always has. (A 401 — expired cookie — is fatal for
// EventSource: no auto-retry until a page reload. Network drops do
// auto-reconnect.)
//
// Dev: /stream is proxied to noc-app by vite.config.ts. Prod: same-origin
// under /app/, Apache `ProxyPass /stream → :8091`.

import { useEffect, useRef, useSyncExternalStore } from "react";
import { useQueryClient } from "@tanstack/react-query";

export interface StreamEvent {
  /** Channel short-name, e.g. "routers", "olts", "router:Bashail". */
  channel: string;
  /** Parsed JSON payload, or the raw string if it wasn't JSON. */
  data: unknown;
}

export type SseStatus = "idle" | "connecting" | "open" | "error";

interface Subscriber {
  channels: Set<string>;
  cb: (ev: StreamEvent) => void;
}

/** The single shared EventSource + the channel ref-counting around it. */
class SseHub {
  private es: EventSource | null = null;
  /** Sorted, comma-joined channel union the current ES is connected to. */
  private currentKey = "";
  private refs = new Map<string, number>();
  private subs = new Set<Subscriber>();
  private status: SseStatus = "idle";
  private statusListeners = new Set<() => void>();
  private reconcilePending = false;

  /** Register a subscriber; returns the unsubscribe fn. */
  subscribe(channels: readonly string[], cb: (ev: StreamEvent) => void): () => void {
    const set = new Set(channels.filter(Boolean));
    const sub: Subscriber = { channels: set, cb };
    this.subs.add(sub);
    for (const ch of set) this.refs.set(ch, (this.refs.get(ch) ?? 0) + 1);
    this.scheduleReconcile();
    return () => {
      if (!this.subs.delete(sub)) return;
      for (const ch of set) {
        const n = (this.refs.get(ch) ?? 1) - 1;
        if (n <= 0) this.refs.delete(ch);
        else this.refs.set(ch, n);
      }
      this.scheduleReconcile();
    };
  }

  getStatus = (): SseStatus => this.status;
  subscribeStatus = (listener: () => void): (() => void) => {
    this.statusListeners.add(listener);
    return () => this.statusListeners.delete(listener);
  };

  private setStatus(s: SseStatus) {
    if (s === this.status) return;
    this.status = s;
    for (const l of this.statusListeners) l();
  }

  private scheduleReconcile() {
    if (this.reconcilePending) return;
    this.reconcilePending = true;
    // Coalesce a burst of subscribe/unsubscribe in one tick (a page
    // mounting several subscribers, StrictMode's double-invoke) into a
    // single reconnect.
    setTimeout(() => {
      this.reconcilePending = false;
      this.reconcile();
    }, 0);
  }

  private reconcile() {
    const key = [...this.refs.keys()].sort().join(",");
    if (key === this.currentKey) return;
    this.currentKey = key;

    if (this.es) {
      this.es.close();
      this.es = null;
    }
    if (key === "") {
      this.setStatus("idle");
      return;
    }

    this.setStatus("connecting");
    const es = new EventSource(`/stream?channels=${encodeURIComponent(key)}`, { withCredentials: true });
    this.es = es;
    es.onopen = () => {
      if (this.es === es) this.setStatus("open");
    };
    es.onerror = () => {
      if (this.es === es) this.setStatus("error");
    };

    const handler = (e: MessageEvent) => {
      let data: unknown = e.data;
      try {
        data = JSON.parse(e.data);
      } catch {
        /* keep the raw string */
      }
      const channel = (e as MessageEvent & { type: string }).type || "message";
      const ev: StreamEvent = { channel, data };
      for (const sub of this.subs) {
        if (sub.channels.has(channel)) {
          try {
            sub.cb(ev);
          } catch {
            /* one bad handler shouldn't stop the fan-out */
          }
        }
      }
    };
    // The server emits each event named after its channel; a generic
    // `message` listener covers anything sent without an explicit name.
    for (const ch of key.split(",")) es.addEventListener(ch, handler as EventListener);
    es.addEventListener("message", handler as EventListener);
  }

  /** Tear everything down (HMR dispose / tests). */
  destroy() {
    if (this.es) {
      this.es.close();
      this.es = null;
    }
    this.subs.clear();
    this.refs.clear();
    this.statusListeners.clear();
    this.currentKey = "";
    this.status = "idle";
  }
}

const hub = new SseHub();

// Don't leak the EventSource across a hot-reload of this module.
if (import.meta.hot) {
  import.meta.hot.dispose(() => hub.destroy());
}

/**
 * Subscribe to a set of /stream channels for this component's lifetime;
 * `onEvent` runs for every event on those channels. The subscription
 * only re-registers when the channel *set* changes (compared order-
 * independently), not when `onEvent`'s identity changes — so a fresh
 * inline callback each render is fine, no useCallback needed.
 */
export function useEventStream(channels: readonly string[], onEvent: (ev: StreamEvent) => void): void {
  const cbRef = useRef(onEvent);
  useEffect(() => {
    cbRef.current = onEvent;
  });

  const key = [...channels].sort().join(",");
  useEffect(() => {
    const list = key === "" ? [] : key.split(",");
    return hub.subscribe(list, (ev) => cbRef.current(ev));
  }, [key]);
}

/** The shared SSE connection state — for the "live data" dot. Reads the
 *  multiplexed hub; does not open a connection of its own. */
export function useSseStatus(): SseStatus {
  return useSyncExternalStore(hub.subscribeStatus, hub.getStatus, hub.getStatus);
}

/** Convenience: true while the shared SSE connection is open. */
export function useStreamConnected(): boolean {
  return useSseStatus() === "open";
}

/**
 * Convenience: on any event from `channels`, invalidate the given query
 * keys (so they re-fetch). Pass a function for `keys` if it needs the
 * event payload (e.g. invalidate only the affected entity).
 */
export function useInvalidateOnEvent(
  channels: readonly string[],
  keys: readonly unknown[][] | ((ev: StreamEvent) => readonly unknown[][]),
): void {
  const qc = useQueryClient();
  useEventStream(channels, (ev) => {
    const list = typeof keys === "function" ? keys(ev) : keys;
    for (const k of list) void qc.invalidateQueries({ queryKey: k });
  });
}
